import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const STATUS_ORDER = ['success', 'failed', 'in_progress', 'pending'];

export default function Compliance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.getCompliance();
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading">Loading compliance data...</div>;
  if (!data) return <div className="empty">No data available.</div>;

  const pct = parseFloat(data.compliancePercent);
  const barColor = pct >= 80 ? 'green' : pct >= 50 ? '' : 'red';

  const filtered = filter === 'all'
    ? data.details
    : data.details.filter(d => d.status === filter);

  // Group by device
  const byDevice = {};
  for (const d of data.details) {
    const host = d.device?.hostname || d.deviceId || 'Unknown';
    if (!byDevice[host]) byDevice[host] = { success: 0, failed: 0, pending: 0, in_progress: 0 };
    byDevice[host][d.status] = (byDevice[host][d.status] || 0) + 1;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Compliance Report</div>
          <div className="page-subtitle">Patch deployment status across all devices</div>
        </div>
        <button className="refresh-btn" onClick={load}>↻ Refresh</button>
      </div>

      {/* Summary cards */}
      <div className="cards-row">
        <div className="card blue" style={{ cursor:'pointer' }} onClick={() => setFilter('all')}>
          <div className="card-label">Total Tasks</div>
          <div className="card-value">{data.total}</div>
          <div className="card-sub">{filter==='all' ? '▼ showing all' : 'click to show all'}</div>
        </div>
        <div className="card green" style={{ cursor:'pointer', outline: filter==='success'?'2px solid #16a34a':undefined }} onClick={() => setFilter(filter==='success'?'all':'success')}>
          <div className="card-label">Success</div>
          <div className="card-value">{data.success}</div>
          <div className="card-sub">{filter==='success' ? '▼ filtered' : 'click to filter'}</div>
        </div>
        <div className="card red" style={{ cursor:'pointer', outline: filter==='failed'?'2px solid #dc2626':undefined }} onClick={() => setFilter(filter==='failed'?'all':'failed')}>
          <div className="card-label">Failed</div>
          <div className="card-value">{data.failed}</div>
          <div className="card-sub">{filter==='failed' ? '▼ filtered' : 'click to filter'}</div>
        </div>
        <div className="card cyan">
          <div className="card-label">Compliance</div>
          <div className="card-value">{data.compliancePercent}%</div>
        </div>
      </div>

      {/* Compliance bar */}
      <div className="table-wrap section-gap" style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Overall Compliance</span>
          <span style={{ fontSize: 14, color: pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444' }}>{data.compliancePercent}%</span>
        </div>
        <div className="progress-bar-wrap">
          <div className={`progress-bar ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Per-device summary */}
      <div className="table-wrap section-gap">
        <div className="table-header"><div className="table-title">Per Device Summary</div></div>
        <table>
          <thead>
            <tr><th>Hostname</th><th>✔ Success</th><th>✘ Failed</th><th>⏳ Pending</th><th>⟳ In Progress</th><th>Compliance</th></tr>
          </thead>
          <tbody>
            {Object.entries(byDevice).map(([host, s]) => {
              const total = s.success + s.failed + s.pending + s.in_progress;
              const devPct = total > 0 ? Math.round((s.success / total) * 100) : 0;
              return (
                <tr key={host}>
                  <td><strong>{host}</strong></td>
                  <td style={{ color: '#22c55e' }}>{s.success}</td>
                  <td style={{ color: '#ef4444' }}>{s.failed}</td>
                  <td style={{ color: '#f59e0b' }}>{s.pending}</td>
                  <td style={{ color: '#60a5fa' }}>{s.in_progress}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="progress-bar-wrap" style={{ width: 80 }}>
                        <div className={`progress-bar ${devPct >= 80 ? 'green' : devPct >= 50 ? '' : 'red'}`} style={{ width: `${devPct}%` }} />
                      </div>
                      <span style={{ fontSize: 12 }}>{devPct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detailed log */}
      <div className="table-wrap">
        <div className="table-header">
          <div className="table-title">Deployment Log</div>
          <div className="flex-gap">
            {['all', ...STATUS_ORDER].map(s => (
              <button key={s} className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(s)}>
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
        </div>
        <table>
          <thead>
            <tr><th>Device</th><th>Patch</th><th>Version</th><th>Status</th><th>Completed</th><th>Output</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="empty">No records.</td></tr>}
            {filtered.map(d => (
              <tr key={d.id}>
                <td>{d.device?.hostname || '—'}</td>
                <td>{d.patch?.name || '—'}</td>
                <td className="text-muted">{d.patch?.version || '—'}</td>
                <td>
                  <span className={`badge ${d.status === 'success' ? 'success' : d.status === 'failed' ? 'failed' : d.status === 'in_progress' ? 'progress' : 'pending'}`}>
                    {d.status === 'success' ? '✔' : d.status === 'failed' ? '✘' : d.status === 'in_progress' ? '⟳' : '⏳'} {d.status}
                  </span>
                </td>
                <td className="text-muted" style={{ fontSize: 12 }}>
                  {d.completedAt ? new Date(d.completedAt).toLocaleString() : '—'}
                </td>
                <td style={{ fontSize: 11, color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.error ? <span style={{ color: '#ef4444' }}>{d.error.substring(0, 60)}</span> : d.output ? d.output.substring(0, 60) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
